/**
 * section-tokens.ts — the single route-to-section-colour map.
 *
 * Ten top-level product areas each own one desaturated hue, defined in
 * `index.css` as `--color-section-*` (`design-tokens.mdc` §6.1). Consumers
 * read this module; **nothing reads a `--color-section-*` token directly.**
 *
 * That indirection is not tidiness for its own sake. Before this module
 * existed the same idea had already fragmented into three conventions in
 * three pages — `PropertiesPage` passed `var(--color-purple-600)`,
 * `DashboardPage` passed `var(--color-teal-500)`, and `VaultLandingPage`
 * passed a raw `#0E7C66` — through a `gradientColor` prop that ~30 call sites
 * each answered differently. Ten sections managed page-by-page would fragment
 * identically, so the prop is retired and the colour resolves from the route.
 *
 * The muted `--color-section-*` hue paints the page-header stripe. The
 * darkened `--color-section-*-active` hue paints the selected sidebar pill
 * so each area has a colour "you are here" without a trailing dot that
 * reads as an unread badge. Page CTAs stay teal.
 */

/** The ten mapped product areas, in sidebar order. */
export const SECTION_IDS = [
  'portfolio',
  'properties',
  'reports',
  'teams',
  'orders',
  'drawing-vault',
  'vault-reports',
  'alerts',
  'support',
  'admin',
] as const;

export type SectionId = (typeof SECTION_IDS)[number];

/**
 * Route-prefix to section, resolved longest-prefix-first.
 *
 * Order in this array does not matter — {@link sectionIdForPath} sorts by
 * prefix length so `/drawings/reports` cannot be shadowed by a shorter entry.
 * Paths absent from this table intentionally fall through to the default teal
 * `--stripe`: account, profile, notifications, messages, search, the document
 * viewers, and the dev pages are chrome rather than product areas.
 */
const SECTION_ROUTE_PREFIXES: Readonly<Record<string, SectionId>> = {
  // All three are dashboard surfaces, so they share the portfolio hue —
  // `/property-dashboard/:id` is a per-building view of the same overview.
  '/dashboard': 'portfolio',
  '/opportunities': 'portfolio',
  '/property-dashboard': 'portfolio',
  '/properties': 'properties',
  '/reports': 'reports',
  // `/analytics/*` redirects into `/reports?tab=…`; mapped so the stripe does
  // not flicker to the default during the redirect.
  '/analytics': 'reports',
  '/teams': 'teams',
  '/orders': 'orders',
  '/work-orders': 'orders',
  '/drawing-vault': 'drawing-vault',
  '/drawings/reports': 'vault-reports',
  '/alerts': 'alerts',
  '/support': 'support',
  // Everything under Admin reads as Admin, including Drawing Vault Admin —
  // the hue answers "where am I in the nav", and that page lives under Admin.
  '/admin': 'admin',
};

const SECTION_PREFIXES_LONGEST_FIRST: ReadonlyArray<readonly [string, SectionId]> =
  Object.entries(SECTION_ROUTE_PREFIXES).sort(([a], [b]) => b.length - a.length);

/** CSS `var()` reference for the default stripe (brand teal). */
export const DEFAULT_STRIPE_VAR = 'var(--stripe)';

/**
 * Strips the query string, hash, and any trailing slash so nav-item paths
 * (`/reports?tab=value-opportunity`) and live locations resolve identically.
 */
function normalizePath(pathname: string): string {
  const path = pathname.split(/[?#]/, 1)[0];
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * The section a route belongs to, or `null` for routes that are app chrome
 * rather than one of the ten product areas.
 *
 * @param pathname - A router pathname or a sidebar nav item's `path`, with or
 *   without a query string.
 */
export function sectionIdForPath(pathname: string): SectionId | null {
  const path = normalizePath(pathname);
  for (const [prefix, id] of SECTION_PREFIXES_LONGEST_FIRST) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return id;
  }
  return null;
}

/**
 * CSS `var()` reference for a section's muted stripe hue.
 *
 * @param id - One of {@link SECTION_IDS}.
 */
export function sectionColorVar(id: SectionId): string {
  return `var(--color-section-${id})`;
}

/**
 * CSS `var()` reference for a section's filled sidebar-pill hue.
 *
 * Darker than {@link sectionColorVar} so white label text clears 4.5:1.
 *
 * @param id - One of {@link SECTION_IDS}.
 */
export function sectionActiveColorVar(id: SectionId): string {
  return `var(--color-section-${id}-active)`;
}

/**
 * The colour a route's page-header stripe should paint, falling back to
 * the brand teal for unmapped routes.
 *
 * @param pathname - A router pathname or a sidebar nav item's `path`.
 * @returns A CSS `var()` reference, never a literal colour.
 */
export function sectionColorForPath(pathname: string): string {
  const id = sectionIdForPath(pathname);
  return id ? sectionColorVar(id) : DEFAULT_STRIPE_VAR;
}

/** CSS `var()` reference for the default sidebar active pill (brand teal). */
export const DEFAULT_SIDEBAR_ACTIVE_VAR = 'var(--color-sidebar-active)';

/**
 * The colour a selected sidebar row should paint, falling back to teal
 * for unmapped chrome routes (account, messages, viewers).
 *
 * @param pathname - A router pathname or a sidebar nav item's `path`.
 * @returns A CSS `var()` reference, never a literal colour.
 */
export function sectionActiveColorForPath(pathname: string): string {
  const id = sectionIdForPath(pathname);
  return id ? sectionActiveColorVar(id) : DEFAULT_SIDEBAR_ACTIVE_VAR;
}
